import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";

import { IDraftRecommendation } from "../../../store/queries/contractModelDrafts";
import { componentTypeLabel } from "../utils";
import { serviceLineOf, serviceLinesIn } from "./steps";

interface IProps {
  recs: IDraftRecommendation[];
  selected: string[];
  busy: boolean;
  onToggle: (line: string) => void;
  onApply: () => void;
}

/**
 * "Find the service lines, and let me pick which ones I actually want to model."
 *
 * Deselecting a line REJECTS its recommendations. That is not a new mechanism — a rejected rec already
 * drops off the implementation checklist — so skipping a service line simply means nobody is ever told
 * to build it. Nothing is deleted, and re-selecting a line restores it to `pending`.
 *
 * Recommendations with no service line (contract-wide terms, routing decisions, whole-contract rules)
 * are never filtered out here. They aren't "a service line" you can decline.
 */
function ServiceLinePicker({
  recs,
  selected,
  busy,
  onToggle,
  onApply,
}: IProps) {
  const lines = serviceLinesIn(recs);

  if (lines.length === 0) {
    return (
      <p className="text-color-secondary">
        The agent did not attribute any recommendation to a named service line,
        so there is nothing to pick from. Everything in this contract is modeled
        as contract-wide.
      </p>
    );
  }

  const recsFor = (line: string) =>
    recs.filter((r) => serviceLineOf(r) === line);
  const skipped = lines.filter((l) => !selected.includes(l));

  return (
    <div>
      <p className="text-color-secondary mt-0">
        The AI found {lines.length} service{" "}
        {lines.length === 1 ? "line" : "lines"} in this contract. Tick the ones
        you want to model. Anything you leave unticked is dropped from the rest
        of the review and never appears on the build checklist.
      </p>

      <div className="grid">
        {lines.map((line) => {
          const inLine = recsFor(line);
          const isSelected = selected.includes(line);
          return (
            <div className="col-12 md:col-6" key={line}>
              <button
                type="button"
                onClick={() => onToggle(line)}
                disabled={busy}
                className={`w-full text-left surface-card border-round p-3 cursor-pointer border-1 ${
                  isSelected
                    ? "border-primary"
                    : "border-transparent surface-border"
                }`}
                style={{ background: "none" }}
              >
                <div className="flex align-items-center gap-2">
                  <Checkbox
                    checked={isSelected}
                    disabled={busy}
                    onChange={() => onToggle(line)}
                  />
                  <span className="font-medium">{line}</span>
                </div>
                <div className="text-sm text-color-secondary mt-2 ml-5">
                  {inLine.length} {inLine.length === 1 ? "item" : "items"} —{" "}
                  {Array.from(
                    new Set(
                      inLine.map((r) => componentTypeLabel(r.componentType))
                    )
                  ).join(", ")}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex align-items-center justify-content-between mt-3">
        <small className="text-color-secondary">
          {skipped.length === 0
            ? "Modeling every service line."
            : `Skipping ${skipped.length}: ${skipped.join(
                ", "
              )}. Their recommendations will be rejected.`}
        </small>
        <Button
          label={busy ? "Applying…" : "Apply selection"}
          icon="pi pi-check"
          disabled={busy}
          onClick={onApply}
        />
      </div>
    </div>
  );
}

export default ServiceLinePicker;
